import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormGroup } from '@angular/forms';
import { concatMap } from 'rxjs';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { format } from 'date-fns';
import { CampaignFieldTypeEnum } from '@www/_models';
import { NetworkService } from '@www/_services/network.service';
import { environment } from '@www-src/environments/environment';
import { buildCampaignFormDataFromValues } from '@www/_helpers/campaign/build-campaign-form-data-from-values';
import { OfflineLead, OfflineLeadStatus, SerializableFormValues } from './offline-lead.model';

const DB_NAME = 'loreal-offline-leads';
const DB_VERSION = 1;
const STORE_NAME = 'leads';

@Injectable({ providedIn: 'root' })
export class OfflineLeadService {
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _networkService = inject(NetworkService);
  private readonly _httpClient = inject(HttpClient);
  private readonly _recaptchaV3Service = inject(ReCaptchaV3Service);
  private readonly _destroyRef = inject(DestroyRef);
  private _db: IDBDatabase | null = null;

  public readonly pendingCount = signal<number>(0);
  public readonly isSyncing = signal<boolean>(false);

  constructor() {
    if (!this._isBrowser) return;
    this._refreshPendingCount();
    this._networkService.onlineChanges$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((online) => {
      if (online) this.syncAll();
    });
  }

  public serializeFormValues(
    form: FormGroup<{
      [id: string]: FormGroup<{
        type: FormControl<CampaignFieldTypeEnum | null>;
        value: FormControl<any>;
        otherValue?: FormControl<string | null>;
      }>;
    }>,
  ): SerializableFormValues {
    const result: SerializableFormValues = {};
    for (const [key, group] of Object.entries(form.controls)) {
      const type = group.controls.type.value!;
      const rawValue = group.controls.value.value;
      const otherValue = group.controls.otherValue?.value ?? null;

      let value: string | number | boolean | string[] | null;
      if (type === CampaignFieldTypeEnum.BIRTHDAY && rawValue) {
        value = format(new Date(rawValue), 'yyyy-MM-dd');
      } else if (type === CampaignFieldTypeEnum.FILE) {
        value = null;
      } else {
        value = rawValue;
      }

      result[key] = { type, value, otherValue };
    }
    return result;
  }

  public async saveLead(slug: string, formValues: SerializableFormValues): Promise<number> {
    const db = await this._openDb();
    const lead: OfflineLead = {
      slug,
      formValues,
      status: 'pending',
      createdAt: Date.now(),
      syncedAt: null,
      errorMessage: null,
    };
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const req = tx.objectStore(STORE_NAME).add(lead);
      req.onsuccess = () => {
        this._refreshPendingCount();
        resolve(req.result as number);
      };
      req.onerror = () => reject(req.error);
    });
  }

  public async getLeads(status?: OfflineLeadStatus): Promise<OfflineLead[]> {
    const db = await this._openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = status ? store.index('status').getAll(status) : store.getAll();
      req.onsuccess = () => resolve(req.result as OfflineLead[]);
      req.onerror = () => reject(req.error);
    });
  }

  public async updateStatus(id: number, status: OfflineLeadStatus, errorMessage?: string): Promise<void> {
    const db = await this._openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const lead = getReq.result as OfflineLead;
        lead.status = status;
        lead.syncedAt = status === 'sent' ? Date.now() : lead.syncedAt;
        lead.errorMessage = errorMessage ?? null;
        const putReq = store.put(lead);
        putReq.onsuccess = () => {
          this._refreshPendingCount();
          resolve();
        };
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    });
  }

  public async syncAll(): Promise<void> {
    if (!this._isBrowser || this.isSyncing()) return;

    // leady utknięte w 'syncing' z przerwanej poprzedniej sesji → reset do 'pending'
    const stuck = await this.getLeads('syncing');
    for (const lead of stuck) {
      await this.updateStatus(lead.id!, 'pending');
    }

    const pending = [
      ...(await this.getLeads('pending')),
      ...(await this.getLeads('failed')),
    ];
    if (!pending.length) return;

    this.isSyncing.set(true);

    try {
      for (const lead of pending) {
        await this.updateStatus(lead.id!, 'syncing');
        try {
          await this._submitLead(lead);
          await this.updateStatus(lead.id!, 'sent');
        } catch (e: any) {
          const isPermanent = e?.status >= 400 && e?.status < 500;
          await this.updateStatus(lead.id!, isPermanent ? 'failed_permanent' : 'failed', this._extractErrorMessage(e));
        }
      }
    } finally {
      this.isSyncing.set(false);
    }
  }

  private _submitLead(lead: OfflineLead): Promise<void> {
    const formData = buildCampaignFormDataFromValues(lead.formValues);
    return new Promise((resolve, reject) => {
      this._recaptchaV3Service
        .execute('importantAction')
        .pipe(
          concatMap((recaptchaKey) =>
            this._httpClient.post(
              `${environment.api}/platform/campaign/lead/${lead.slug}/submit`,
              formData,
              { params: { recaptchaKey } },
            ),
          ),
        )
        .subscribe({ next: () => resolve(), error: reject });
    });
  }

  private _extractErrorMessage(e: any): string {
    // HttpErrorResponse: e.status = HTTP code, e.error = response body
    if (e?.status) {
      const bodyMessage =
        e.error?.message ??
        e.error?.errors?.errors?.[0] ??
        null;
      return bodyMessage
        ? `HTTP ${e.status}: ${bodyMessage}`
        : `HTTP ${e.status} ${e.statusText ?? ''}`.trim();
    }
    return e?.message ?? 'Błąd synchronizacji';
  }

  private async _openDb(): Promise<IDBDatabase> {
    if (this._db) return this._db;
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
          store.createIndex('slug', 'slug', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }
      };
      req.onsuccess = (event) => {
        this._db = (event.target as IDBOpenDBRequest).result;
        resolve(this._db);
      };
      req.onerror = () => reject(req.error);
    });
  }

  private async _refreshPendingCount(): Promise<void> {
    try {
      const pending = await this.getLeads('pending');
      const failed = await this.getLeads('failed');
      this.pendingCount.set(pending.length + failed.length);
    } catch {
      // ignore
    }
  }
}
